import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Filter,
  Target, Video, Users, Package, TrendingUp, Rocket, Megaphone,
  FileText, Clock, MessageSquare, Zap, Play, CheckCircle2, Pause,
  ExternalLink, Building2, DollarSign, BarChart3, Layers,
  Search, Download, Check, Settings, Sparkles, UserCheck,
  LayoutGrid, List
} from 'lucide-react';
import axios from 'axios';
import { useWebSocket } from '@/contexts/WebSocketContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from 'react-router-dom';

// Campaign type configurations
const campaignTypes = {
  challenge: { label: 'Challenge/Contest', icon: Target, color: '#E63946' },
  webinar: { label: 'Webinar/Seminar', icon: Video, color: '#3B82F6' },
  bootcamp: { label: 'Bootcamp/Class', icon: Users, color: '#10B981' },
  promo: { label: 'Promo/Offer', icon: Package, color: '#F59E0B' },
  grow: { label: 'Grow Together', icon: TrendingUp, color: '#8B5CF6' },
  product: { label: 'Product Launching', icon: Rocket, color: '#EC4899' },
  other: { label: 'Other', icon: Megaphone, color: '#6B7280' },
};

// Campaign Lifecycle Phases
const campaignPhases = [
  { id: 'planning', label: 'Planning', icon: FileText, color: '#6B7280' },
  { id: 'preparation', label: 'Preparation', icon: Settings, color: '#F59E0B' },
  { id: 'prospecting', label: 'Prospecting', icon: Users, color: '#8B5CF6' },
  { id: 'seeding', label: 'Seeding', icon: Sparkles, color: '#06B6D4' },
  { id: 'buzz', label: 'Buzz', icon: Zap, color: '#EC4899' },
  { id: 'launching', label: 'Launching', icon: Megaphone, color: '#F97316' },
  { id: 'running', label: 'Running', icon: Play, color: '#10B981' },
  { id: 'closing', label: 'Closing', icon: Target, color: '#14B8A6' },
];

const statusConfig = {
  planning: { label: 'Planning', color: '#6B7280', icon: FileText, phase: 1 },
  preparation: { label: 'Preparation', color: '#F59E0B', icon: Clock, phase: 2 },
  prospecting: { label: 'Prospecting', color: '#8B5CF6', icon: Users, phase: 3 },
  seeding: { label: 'Seeding', color: '#06B6D4', icon: MessageSquare, phase: 4 },
  buzz: { label: 'Buzz', color: '#EC4899', icon: Zap, phase: 5 },
  launching: { label: 'Launching', color: '#F97316', icon: Megaphone, phase: 6 },
  running: { label: 'Running', color: '#10B981', icon: Play, phase: 7 },
  closing: { label: 'Closing', color: '#14B8A6', icon: Target, phase: 8 },
  completed: { label: 'Completed', color: '#3B82F6', icon: CheckCircle2, phase: 9 },
  kiv: { label: 'KIV', color: '#E63946', icon: Pause, phase: 0 },
};

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Get today's date info
const today = new Date();
const currentDay = today.getDate();
const currentMonth = today.getMonth();
const currentYearNow = today.getFullYear();

// IB list for filter - loaded from backend dynamically

// Quick filters
const quickFilters = [
  { id: 'all', label: 'All' },
  { id: 'this-month', label: 'This Month' },
  { id: 'running', label: 'Running' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'critical', label: 'Critical' },
];

// Campaign List Popup
const CampaignListPopup = ({ title, campaigns, open, onClose, onCampaignClick, color }) => {
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gradient-to-br from-[#0F0F14] to-[#0A0A0E] border-[#252530] max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: `${color}20`, border: `2px solid ${color}40` }}
            >
              <Layers size={22} style={{ color }} />
            </div>
            <div>
              <span className="text-lg font-bold">{title}</span>
              <p className="text-xs text-[#6B7280] font-normal mt-0.5">{campaigns.length} campaigns</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-2 max-h-[500px] overflow-y-auto pr-2">
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-[#6B7280]">
              <Layers size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No campaigns</p>
            </div>
          ) : (
            campaigns.map(campaign => {
              const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
              const statusConf = statusConfig[campaign.status] || statusConfig.planning;
              const TypeIcon = typeConfig.icon;
              
              return (
                <div 
                  key={campaign.id}
                  onClick={() => onCampaignClick(campaign)}
                  className="p-4 rounded-2xl bg-gradient-to-r from-[#1A1A22] to-[#15151A] border border-[#252530] hover:border-[#3A3A45] cursor-pointer transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${typeConfig.color}20` }}
                    >
                      <TypeIcon size={20} style={{ color: typeConfig.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white group-hover:text-[#E63946] transition-colors">
                        {campaign.name}
                      </p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#252530]">
                          <Building2 size={10} className="text-[#6B7280]" />
                          <span className="text-[10px] text-[#9CA3AF]">{campaign.ibName}</span>
                        </div>
                        <Badge 
                          className="text-[10px] px-2 rounded-full"
                          style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}
                        >
                          {statusConf.label}
                        </Badge>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-[#4A4A5A] group-hover:text-[#E63946] flex-shrink-0" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Phase Timeline Visual
const PhaseTimelineVisual = ({ currentStatus }) => {
  const currentPhaseIdx = campaignPhases.findIndex(p => p.id === currentStatus);
  
  return (
    <div className="relative py-4 px-2">
      <div className="absolute top-[42px] left-[5%] right-[5%] h-1.5 bg-[#252530] rounded-full" />
      {currentPhaseIdx >= 0 && (
        <div 
          className="absolute top-[42px] left-[5%] h-1.5 bg-gradient-to-r from-[#10B981] via-[#06B6D4] to-[#3B82F6] rounded-full"
          style={{ width: `${Math.min((currentPhaseIdx / (campaignPhases.length - 1)) * 90, 90)}%` }} 
        />
      )}
      
      <div className="relative flex justify-between">
        {campaignPhases.map((phase, idx) => {
          const isCompleted = idx < currentPhaseIdx;
          const isCurrent = phase.id === currentStatus;
          const Icon = phase.icon;
          
          return (
            <div key={phase.id} className="flex flex-col items-center relative" style={{ minWidth: '60px' }}>
              {isCurrent && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                  <span className="text-[9px] bg-gradient-to-r from-[#E63946] to-[#F97316] text-white px-2 py-0.5 rounded-full shadow font-semibold">
                    📍 Current
                  </span>
                </div>
              )}
              
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center z-10 shadow-lg ${
                isCompleted ? 'bg-gradient-to-br from-[#10B981] to-[#059669]' :
                isCurrent ? 'bg-gradient-to-br from-[#E63946] to-[#DC2626] ring-2 ring-[#E63946]/30' :
                'bg-[#1A1A22]'
              }`}
                style={{ 
                  backgroundColor: !isCompleted && !isCurrent ? `${phase.color}25` : undefined,
                  borderColor: !isCompleted && !isCurrent ? phase.color : undefined,
                  borderWidth: !isCompleted && !isCurrent ? '2px' : undefined
                }}>
                {isCompleted ? <Check size={16} className="text-white" /> :
                 <Icon size={14} style={{ color: isCurrent ? 'white' : phase.color }} />}
              </div>
              
              <p className={`text-[9px] mt-1.5 text-center font-semibold ${
                isCompleted ? 'text-[#10B981]' : 
                isCurrent ? 'text-[#E63946]' : 'text-[#6B7280]'
              }`}>{phase.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Campaign Detail Modal
const CampaignDetailModal = ({ campaign, open, onClose, onNavigate }) => {
  if (!campaign) return null;
  
  const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
  const TypeIcon = typeConfig.icon;
  
  const kpiProgress = campaign.kpis?.length > 0 
    ? campaign.kpis.filter(k => parseFloat(k.current) > 0).length 
    : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gradient-to-br from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] border-[#252530] max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 z-20 bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/10 to-transparent border-b border-[#252530] px-6 py-4 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border-2 border-[#E63946]/40">
                <Layers size={28} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Campaign Blueprint</h2>
                <p className="text-xs text-[#9CA3AF]">{campaign.ibName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="border-[#252530] text-[#9CA3AF] rounded-xl">
                <Download size={14} className="mr-1.5" />
                PDF
              </Button>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="bg-gradient-to-r from-[#1A1A22] to-[#15151A] rounded-2xl border border-[#252530] p-4">
            <PhaseTimelineVisual currentStatus={campaign.status} />
          </div>

          <div className="bg-gradient-to-r from-[#1A1A22] to-[#15151A] rounded-2xl border border-[#252530] overflow-hidden">
            <div className="p-5 border-b border-[#252530]">
              <div className="flex items-start gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${typeConfig.color}20`, border: `2px solid ${typeConfig.color}` }}
                >
                  <TypeIcon size={32} style={{ color: typeConfig.color }} />
                </div>
                <div className="flex-1">
                  <Badge className="text-[10px] mb-2 rounded-full" style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}>
                    {typeConfig.label}
                  </Badge>
                  <h3 className="text-xl font-bold text-white">{campaign.name}</h3>
                  <p className="text-sm text-[#9CA3AF] mt-1">{campaign.description}</p>
                </div>
              </div>
            </div>

            <div className="p-5 grid grid-cols-4 gap-4">
              <div className="bg-[#0F0F14] rounded-2xl p-4 border border-[#252530] text-center">
                <p className="text-3xl font-black text-white">{kpiProgress}</p>
                <p className="text-[10px] text-[#6B7280] mt-1">KPIs Set</p>
              </div>
              <div className="bg-[#10B981]/10 rounded-2xl p-4 border border-[#10B981]/20 text-center">
                <p className="text-3xl font-black text-[#10B981]">{campaign.materialsReady}/{campaign.materialsTotal}</p>
                <p className="text-[10px] text-[#6B7280] mt-1">Materials Ready</p>
              </div>
              <div className="bg-[#0F0F14] rounded-2xl p-4 border border-[#252530] text-center">
                <p className="text-3xl font-black text-white">{campaign.team?.length || 0}</p>
                <p className="text-[10px] text-[#6B7280] mt-1">Team Assigned</p>
              </div>
              <div className="bg-[#8B5CF6]/10 rounded-2xl p-4 border border-[#8B5CF6]/20 text-center">
                <p className="text-3xl font-black text-[#8B5CF6]">{campaign.phasesPlanned}/{campaign.phasesTotal}</p>
                <p className="text-[10px] text-[#6B7280] mt-1">Phases Planned</p>
              </div>
            </div>

            <div className="px-5 pb-5">
              <div className="bg-gradient-to-r from-[#F59E0B]/10 to-transparent rounded-2xl p-4 border border-[#F59E0B]/30">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                    <DollarSign size={24} className="text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#6B7280] uppercase">Total Budget</p>
                    <p className="text-2xl font-black text-white">{campaign.budget}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {campaign.objective && (
            <div className="bg-gradient-to-r from-[#1A1A22] to-[#15151A] rounded-2xl border border-[#252530] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#252530] bg-gradient-to-r from-[#F97316]/15 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#F97316]/20 flex items-center justify-center">
                    <Target size={18} className="text-[#F97316]" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Objective & KPIs</h4>
                </div>
              </div>
              <div className="p-5">
                <div className="p-4 bg-[#0F0F14] rounded-xl border border-[#252530] mb-4">
                  <p className="text-[10px] text-[#6B7280] mb-1 uppercase">Main Objective</p>
                  <p className="text-sm text-white">{campaign.objective}</p>
                </div>
                {campaign.kpis?.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {campaign.kpis.map((kpi, idx) => (
                      <div key={kpi.metric || idx} className="p-4 bg-[#0F0F14] rounded-xl border border-[#252530]">
                        <p className="text-[10px] text-[#6B7280]">{kpi.metric}</p>
                        <p className="text-xl font-black text-white mt-1">{kpi.current || '0'}</p>
                        <p className="text-[10px] text-[#9CA3AF]">Target: {kpi.target}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {campaign.team?.length > 0 && (
            <div className="bg-gradient-to-r from-[#1A1A22] to-[#15151A] rounded-2xl border border-[#252530] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#252530] bg-gradient-to-r from-[#3B82F6]/15 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                    <UserCheck size={18} className="text-[#3B82F6]" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Team & PIC</h4>
                </div>
              </div>
              <div className="p-5 grid grid-cols-3 gap-3">
                {campaign.team.map((member, idx) => (
                  <div key={member.name || idx} className="p-3 bg-[#0F0F14] rounded-xl border border-[#252530] flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                      <Users size={16} className="text-[#3B82F6]" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">{member.name || 'TBA'}</p>
                      <p className="text-[10px] text-[#6B7280]">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1 border-[#252530] text-[#9CA3AF] hover:text-white rounded-xl h-11"
              onClick={onClose}
            >
              Close
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-[#E63946] to-[#D62839] text-white rounded-xl h-11 font-bold"
              onClick={() => onNavigate(campaign)}
              data-testid="view-full-details-btn"
            >
              <ExternalLink size={14} className="mr-2" />
              View Full Details
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ListOfCampaignPage() {
  const [currentYear, setCurrentYear] = useState(2026);
  const [viewLevel, setViewLevel] = useState('timeline');
  const [viewMode, setViewMode] = useState('timeline');
  const [selectedQuarter, setSelectedQuarter] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [filterIB, setFilterIB] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statsPopup, setStatsPopup] = useState({ open: false, type: null });
  const navigate = useNavigate();
  const { subscribe } = useWebSocket() || {};

  // Load all campaigns from MongoDB backend
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [ibList, setIbList] = useState([{ id: 'all', name: 'All IBs' }]);
  
  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [campaignsRes, ibsRes] = await Promise.all([
        axios.get(`${API}/campaigns/all`, { headers }),
        axios.get(`${API}/ib/list`, { headers }),
      ]);
      
      if (campaignsRes.data?.campaigns) {
        const campaigns = campaignsRes.data.campaigns.map(c => ({
          ...c,
          materialsReady: c.materialsReady || 0,
          materialsTotal: c.materialsTotal || 17,
          phasesPlanned: c.phasesPlanned || 0,
          phasesTotal: c.phasesTotal || 8,
          budget: c.budget || 'RM 0',
          budgetNum: c.budgetNum || 0,
        }));
        setAllCampaigns(campaigns);
      }
      
      if (Array.isArray(ibsRes.data)) {
        setIbList([
          { id: 'all', name: 'All IBs' },
          ...ibsRes.data.map(ib => ({ id: ib.id, name: ib.name })),
        ]);
      }
    } catch (e) {
      console.error('Failed to load campaigns:', e);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Auto-refresh when WebSocket receives campaign update
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((msg) => {
      if (msg.type === 'data_updated' && msg.page?.toLowerCase().includes('campaign')) {
        fetchCampaigns();
      }
    });
  }, [subscribe, fetchCampaigns]);

  // Export campaigns to CSV
  const handleExport = () => {
    const headers = ['Campaign Name', 'IB Name', 'Type', 'Status', 'Start Date', 'End Date', 'Target KPI', 'Actual KPI', 'Budget'];
    const rows = filteredCampaigns.map(c => [
      c.name,
      c.ibName,
      campaignTypes[c.type]?.label || c.type,
      statusConfig[c.status]?.label || c.status,
      c.startDate,
      c.endDate,
      c.targetKPI || '-',
      c.actualKPI || '-',
      c.budget
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `campaigns_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter campaigns - using real data from allCampaigns
  const filteredCampaigns = useMemo(() => {
    let result = allCampaigns.filter(c => {
      const matchIB = filterIB === 'all' || c.ibId === filterIB;
      const matchStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchSearch = !searchQuery || 
        (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.ibName || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchIB && matchStatus && matchSearch;
    });

    if (quickFilter === 'this-month') {
      result = result.filter(c => {
        if (!c.startDate || !c.endDate) return false;
        const start = new Date(c.startDate);
        const end = new Date(c.endDate);
        return start.getMonth() === currentMonth || end.getMonth() === currentMonth;
      });
    } else if (quickFilter === 'running') {
      result = result.filter(c => c.status === 'running');
    } else if (quickFilter === 'upcoming') {
      result = result.filter(c => ['planning', 'preparation'].includes(c.status));
    } else if (quickFilter === 'critical') {
      result = result.filter(c => c.materialsReady < c.materialsTotal * 0.5 && c.status !== 'completed');
    }

    return result;
  }, [allCampaigns, filterIB, filterStatus, searchQuery, quickFilter]);

  // Get campaigns for year (only ones with dates for timeline)
  const yearCampaigns = useMemo(() => {
    return filteredCampaigns.filter(c => {
      if (!c.startDate || !c.endDate) return false;
      const startYear = new Date(c.startDate).getFullYear();
      const endYear = new Date(c.endDate).getFullYear();
      return startYear === currentYear || endYear === currentYear;
    });
  }, [filteredCampaigns, currentYear]);

  // Stats
  const runningCampaigns = filteredCampaigns.filter(c => c.status === 'running');
  const upcomingCampaigns = filteredCampaigns.filter(c => ['planning', 'preparation'].includes(c.status));

  // Get campaigns for period
  const getCampaignsForPeriod = (startDate, endDate) => {
    return filteredCampaigns.filter(c => {
      if (!c.startDate || !c.endDate) return false;
      const cStart = new Date(c.startDate);
      const cEnd = new Date(c.endDate);
      return cStart <= endDate && cEnd >= startDate;
    });
  };

  // FIXED: Proper swim lanes grouping with visual overlap detection
  const groupIntoSwimLanes = (campaigns, yearStart, yearEnd) => {
    const rows = [];
    // Sort by effective start date (within view bounds)
    const sorted = [...campaigns].sort((a, b) => {
      const aStart = Math.max(new Date(a.startDate).getTime(), yearStart?.getTime() || 0);
      const bStart = Math.max(new Date(b.startDate).getTime(), yearStart?.getTime() || 0);
      return aStart - bStart;
    });
    
    // Calculate minimum visual width in days (minWidth 30px in ~1400px container for 365 days)
    const totalDays = yearStart && yearEnd 
      ? Math.ceil((yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
      : 365;
    // minWidth visual represents approximately this many days (~3% of timeline)
    const minWidthDays = Math.ceil(totalDays * 0.025); // ~2.5% of timeline = ~9 days for year
    
    sorted.forEach(campaign => {
      // Use effective dates within the view bounds
      const cStart = yearStart ? Math.max(new Date(campaign.startDate).getTime(), yearStart.getTime()) : new Date(campaign.startDate).getTime();
      const cEnd = yearEnd ? Math.min(new Date(campaign.endDate).getTime(), yearEnd.getTime()) : new Date(campaign.endDate).getTime();
      
      // Calculate visual end considering minWidth
      const actualDuration = (cEnd - cStart) / (1000 * 60 * 60 * 24);
      const visualDuration = Math.max(actualDuration, minWidthDays);
      const visualEnd = cStart + (visualDuration * 24 * 60 * 60 * 1000);
      
      let placed = false;
      for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
        let canFit = true;
        for (let existing of rows[rowIdx]) {
          const eStart = yearStart ? Math.max(new Date(existing.startDate).getTime(), yearStart.getTime()) : new Date(existing.startDate).getTime();
          const eEnd = yearEnd ? Math.min(new Date(existing.endDate).getTime(), yearEnd.getTime()) : new Date(existing.endDate).getTime();
          
          // Calculate visual end for existing campaign
          const existingDuration = (eEnd - eStart) / (1000 * 60 * 60 * 24);
          const existingVisualDuration = Math.max(existingDuration, minWidthDays);
          const existingVisualEnd = eStart + (existingVisualDuration * 24 * 60 * 60 * 1000);
          
          // Check if visual bars would overlap (add 1 day gap for visual spacing)
          const gap = 1 * 24 * 60 * 60 * 1000; // 1 day gap
          if (!(visualEnd + gap < eStart || cStart > existingVisualEnd + gap)) {
            canFit = false;
            break;
          }
        }
        if (canFit) {
          rows[rowIdx].push(campaign);
          placed = true;
          break;
        }
      }
      if (!placed) {
        rows.push([campaign]);
      }
    });
    return rows;
  };

  // Navigation
  const goBack = () => {
    if (viewLevel === 'week') setViewLevel('month');
    else if (viewLevel === 'month') setViewLevel('quarter');
    else if (viewLevel === 'quarter') setViewLevel('timeline');
  };

  const drillDown = (type, value) => {
    if (type === 'quarter') { setSelectedQuarter(value); setViewLevel('quarter'); }
    else if (type === 'month') { setSelectedMonth(value); setViewLevel('month'); }
    else if (type === 'week') { setSelectedWeek(value); setViewLevel('week'); }
  };

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
    setStatsPopup({ open: false, type: null });
  };

  const handleNavigateToIB = (campaign) => {
    localStorage.setItem('selectedIB', campaign.ibId);
    window.dispatchEvent(new Event('ibChanged'));
    navigate(`/ib/${campaign.ibId}/details`);
    setShowDetailModal(false);
  };

  const handleStatsClick = (type) => setStatsPopup({ open: true, type });

  const getStatsPopupData = () => {
    switch (statsPopup.type) {
      case 'running': return { title: 'Running Campaigns', campaigns: runningCampaigns, color: '#10B981' };
      case 'upcoming': return { title: 'Upcoming Campaigns', campaigns: upcomingCampaigns, color: '#F59E0B' };
      case 'total': return { title: 'All Campaigns', campaigns: filteredCampaigns, color: '#3B82F6' };
      default: return { title: '', campaigns: [], color: '#6B7280' };
    }
  };

  // Render Year Timeline View - FIXED SWIM LANES
  const renderTimelineView = () => {
    const yearStart = new Date(currentYear, 0, 1);
    const yearEnd = new Date(currentYear, 11, 31);
    const totalDays = 365;
    const rows = groupIntoSwimLanes(yearCampaigns, yearStart, yearEnd);
    const isCurrentYear = currentYear === currentYearNow;

    return (
      <div className="bg-gradient-to-br from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] rounded-2xl border border-[#1E1E26] overflow-hidden shadow-2xl">
        {/* Month Headers with current month indicator */}
        <div className="flex border-b border-[#252530] bg-gradient-to-r from-[#1A1A22] to-[#15151A]">
          {monthNames.map((month, idx) => {
            const monthCampaigns = getCampaignsForPeriod(
              new Date(currentYear, idx, 1),
              new Date(currentYear, idx + 1, 0)
            );
            const isCurrentMonthHighlight = isCurrentYear && idx === currentMonth;
            
            return (
              <div 
                key={idx} 
                className={`flex-1 py-3 px-1 text-center border-r border-[#252530]/50 last:border-r-0 cursor-pointer transition-all ${
                  isCurrentMonthHighlight 
                    ? 'bg-gradient-to-b from-[#E63946]/20 to-transparent' 
                    : 'hover:bg-[#252530]/50'
                }`}
                onClick={() => drillDown('month', idx)}
              >
                <p className={`text-xs font-bold ${isCurrentMonthHighlight ? 'text-[#E63946]' : 'text-white'}`}>
                  {month}
                  {isCurrentMonthHighlight && <span className="ml-1">●</span>}
                </p>
                <p className="text-[10px] text-[#6B7280] mt-0.5">{monthCampaigns.length}</p>
              </div>
            );
          })}
        </div>

        {/* Quarter Indicators */}
        <div className="flex border-b border-[#1E1E26]">
          {['Q1', 'Q2', 'Q3', 'Q4'].map((q, idx) => {
            const quarterStartMonth = idx * 3;
            const isCurrentQuarter = isCurrentYear && currentMonth >= quarterStartMonth && currentMonth < quarterStartMonth + 3;
            return (
              <div 
                key={q} 
                className={`flex-1 py-2 text-center border-r border-[#1E1E26] last:border-r-0 cursor-pointer transition-all ${
                  isCurrentQuarter 
                    ? 'bg-[#E63946]/10' 
                    : 'hover:bg-[#E63946]/5'
                }`}
                onClick={() => drillDown('quarter', idx)}
              >
                <span className={`text-[11px] font-black ${isCurrentQuarter ? 'text-[#E63946]' : 'text-[#E63946]/70'}`}>
                  {q}
                </span>
              </div>
            );
          })}
        </div>

        {/* Campaign Rows - PROPER SWIM LANES */}
        <div className="p-5 min-h-[300px] relative">
          {/* Today indicator line */}
          {isCurrentYear && (
            <div 
              className="absolute top-0 bottom-0 w-0.5 bg-[#E63946] z-30 opacity-60"
              style={{ 
                left: `calc(${((new Date(currentYear, currentMonth, currentDay) - yearStart) / (1000 * 60 * 60 * 24) / totalDays) * 100}% + 20px)` 
              }}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#E63946] rounded-full" />
            </div>
          )}

          {rows.length === 0 ? (
            <div className="flex items-center justify-center h-[250px] text-[#6B7280]">
              <div className="text-center">
                <CalendarIcon size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-sm font-medium">No campaigns for {currentYear}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row, rowIdx) => (
                <div key={rowIdx} className="relative h-12">
                  {row.map(campaign => {
                    const cStart = new Date(campaign.startDate);
                    const cEnd = new Date(campaign.endDate);
                    const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
                    const TypeIcon = typeConfig.icon;

                    const effectiveStart = cStart < yearStart ? yearStart : cStart;
                    const effectiveEnd = cEnd > yearEnd ? yearEnd : cEnd;
                    const startDay = Math.floor((effectiveStart - yearStart) / (1000 * 60 * 60 * 24));
                    const endDay = Math.floor((effectiveEnd - yearStart) / (1000 * 60 * 60 * 24));
                    const left = (startDay / totalDays) * 100;
                    const width = ((endDay - startDay + 1) / totalDays) * 100;

                    return (
                      <div
                        key={campaign.id}
                        className="absolute h-10 rounded-xl cursor-pointer transition-all hover:scale-[1.02] hover:z-20 group"
                        style={{
                          left: `${left}%`,
                          width: `${Math.max(width, 1.5)}%`,
                          minWidth: '30px',
                          background: `linear-gradient(135deg, ${typeConfig.color}40 0%, ${typeConfig.color}20 100%)`,
                          border: `2px solid ${typeConfig.color}`,
                          boxShadow: `0 4px 15px ${typeConfig.color}30`,
                        }}
                        onClick={() => handleCampaignClick(campaign)}
                        data-testid={`campaign-bar-${campaign.id}`}
                      >
                        <div className="h-full px-1 flex items-center gap-0.5 overflow-hidden">
                          <TypeIcon size={10} style={{ color: typeConfig.color }} className="flex-shrink-0" />
                          <div className="flex-1 min-w-0 hidden sm:block">
                            <span className="text-[8px] font-bold text-white truncate block">{campaign.name}</span>
                            <span className="text-[7px] text-white/60 truncate block">{campaign.ibName}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="px-5 py-3 border-t border-[#1E1E26] bg-[#0A0A0D]">
          <div className="flex flex-wrap gap-4">
            {Object.entries(campaignTypes).map(([key, config]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                <span className="text-[10px] text-[#6B7280]">{config.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render List View with Target & Actual columns
  const renderListView = () => {
    // Show ALL filtered campaigns in list view (not just year-filtered)
    const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
      if (!a.startDate && !b.startDate) return 0;
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(a.startDate) - new Date(b.startDate);
    });

    return (
      <div className="bg-gradient-to-br from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] rounded-2xl border border-[#1E1E26] overflow-hidden shadow-2xl overflow-x-auto">
        {/* Table Header */}
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="bg-gradient-to-r from-[#1A1A22] to-[#15151A] border-b border-[#252530]">
              <th className="px-4 py-3 text-left text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[22%]">Campaign</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[12%]">IB</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[14%]">Duration</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[10%]">Status</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[14%]">Target</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[14%]">Actual</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold text-[#6B7280] uppercase tracking-wider w-[14%]">Budget</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1E1E26]">
            {sortedCampaigns.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-16 text-[#6B7280]">
                  <List size={48} className="mx-auto mb-4 opacity-30" />
                  <p className="text-sm">No campaigns found</p>
                </td>
              </tr>
            ) : (
              sortedCampaigns.map((campaign, idx) => {
                const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
                const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                const TypeIcon = typeConfig.icon;
                const hasDate = campaign.startDate && campaign.endDate;
                const startDate = hasDate ? new Date(campaign.startDate) : null;
                const endDate = hasDate ? new Date(campaign.endDate) : null;
                const duration = hasDate ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1 : 0;

                return (
                  <tr 
                    key={campaign.id || idx}
                    className="hover:bg-[#1A1A22]/50 cursor-pointer transition-all"
                    onClick={() => handleCampaignClick(campaign)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${typeConfig.color}20` }}
                        >
                          <TypeIcon size={14} style={{ color: typeConfig.color }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white truncate">{campaign.name}</p>
                          <p className="text-[9px] text-[#6B7280]">{typeConfig.label}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-[#9CA3AF]">{campaign.ibName || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {hasDate ? (
                        <>
                          <p className="text-[10px] text-white">{startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                          <p className="text-[9px] text-[#6B7280]">{duration} days</p>
                        </>
                      ) : (
                        <p className="text-[10px] text-[#6B7280]">No date set</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge 
                        className="text-[8px] px-1.5 rounded-full"
                        style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}
                      >
                        {statusConf.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[10px] text-[#9CA3AF]">{campaign.targetKPI || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[10px] text-[#10B981] font-medium">{campaign.actualKPI || '-'}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-bold text-white">{campaign.budget || '-'}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Quarter View - FIXED SWIM LANES
  const renderQuarterView = () => {
    const quarterMonths = [selectedQuarter * 3, selectedQuarter * 3 + 1, selectedQuarter * 3 + 2];
    const isCurrentQuarter = currentYear === currentYearNow && currentMonth >= selectedQuarter * 3 && currentMonth < (selectedQuarter + 1) * 3;
    
    return (
      <div className="space-y-4">
        {quarterMonths.map((monthIndex) => {
          const mStart = new Date(currentYear, monthIndex, 1);
          const mEnd = new Date(currentYear, monthIndex + 1, 0);
          const monthCampaigns = getCampaignsForPeriod(mStart, mEnd);
          const totalDays = mEnd.getDate();
          const rows = groupIntoSwimLanes(monthCampaigns, mStart, mEnd);
          const isCurrentMonthInQuarter = currentYear === currentYearNow && monthIndex === currentMonth;

          return (
            <div key={monthIndex} className="bg-gradient-to-br from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] rounded-2xl border border-[#1E1E26] overflow-hidden shadow-xl">
              <div 
                className={`px-5 py-3 border-b border-[#252530] flex items-center justify-between cursor-pointer transition-all ${
                  isCurrentMonthInQuarter 
                    ? 'bg-gradient-to-r from-[#E63946]/20 via-[#E63946]/10 to-transparent' 
                    : 'bg-gradient-to-r from-[#1A1A22] to-[#15151A] hover:from-[#252530]'
                }`}
                onClick={() => drillDown('month', monthIndex)}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-lg font-black ${isCurrentMonthInQuarter ? 'text-[#E63946]' : 'text-white'}`}>
                    {fullMonthNames[monthIndex]}
                    {isCurrentMonthInQuarter && <span className="ml-2 text-sm">● Today</span>}
                  </span>
                  <span className="text-xs text-[#6B7280]">{currentYear}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30 rounded-full px-3">
                    {monthCampaigns.length} Campaigns
                  </Badge>
                  <ChevronRight size={16} className="text-[#6B7280]" />
                </div>
              </div>

              {/* Day headers with today indicator */}
              <div className="px-5 py-2 border-b border-[#1E1E26] flex bg-[#0A0A0D]/50">
                <div className="w-44 flex-shrink-0" />
                <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${totalDays}, 1fr)` }}>
                  {Array.from({ length: totalDays }, (_, i) => {
                    const isToday = isCurrentMonthInQuarter && (i + 1) === currentDay;
                    return (
                      <div 
                        key={i} 
                        className={`text-center text-[8px] font-medium ${
                          isToday ? 'text-[#E63946] font-bold' : 'text-[#4A4A5A]'
                        }`}
                      >
                        {i + 1}
                        {isToday && <div className="w-1 h-1 bg-[#E63946] rounded-full mx-auto mt-0.5" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Campaign rows - FIXED */}
              <div className="p-4 space-y-2 relative">
                {/* Today vertical line */}
                {isCurrentMonthInQuarter && (
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-[#E63946]/50 z-20"
                    style={{ left: `calc(176px + ${((currentDay - 1) / totalDays) * (100 - (176 * 100 / (typeof window !== 'undefined' ? window.innerWidth : 1920)))}%)` }}
                  />
                )}

                {rows.length === 0 ? (
                  <div className="text-center py-8 text-[#6B7280] text-sm">No campaigns this month</div>
                ) : (
                  rows.map((row, rowIdx) => (
                    <div key={rowIdx} className="flex items-center">
                      <div className="w-44 flex-shrink-0 pr-3">
                        {row[0] && (
                          <>
                            <p className="text-xs font-bold text-white truncate">{row[0].name}</p>
                            <p className="text-[10px] text-[#9CA3AF] truncate">{row[0].ibName}</p>
                          </>
                        )}
                      </div>
                      <div className="flex-1 relative h-10">
                        {row.map((campaign) => {
                          const cStart = new Date(campaign.startDate);
                          const cEnd = new Date(campaign.endDate);
                          const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
                          const TypeIcon = typeConfig.icon;
                          const startDay = cStart.getMonth() === monthIndex ? cStart.getDate() : 1;
                          const endDay = cEnd.getMonth() === monthIndex ? cEnd.getDate() : totalDays;
                          const duration = endDay - startDay + 1;
                          const left = ((startDay - 1) / totalDays) * 100;
                          const width = (duration / totalDays) * 100;

                          return (
                            <div
                              key={campaign.id}
                              className="absolute h-8 top-1 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:z-10"
                              style={{
                                left: `${left}%`,
                                width: `${Math.max(width, 3)}%`,
                                minWidth: '40px',
                                background: `linear-gradient(135deg, ${typeConfig.color}40 0%, ${typeConfig.color}20 100%)`,
                                border: `2px solid ${typeConfig.color}`,
                              }}
                              onClick={() => handleCampaignClick(campaign)}
                            >
                              <div className="h-full px-1.5 flex items-center gap-1 overflow-hidden">
                                <TypeIcon size={10} style={{ color: typeConfig.color }} />
                                <span className="text-[8px] font-bold text-white truncate">{campaign.name}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const mStart = new Date(currentYear, selectedMonth, 1);
    const mEnd = new Date(currentYear, selectedMonth + 1, 0);
    const monthCampaigns = getCampaignsForPeriod(mStart, mEnd);
    const isCurrentMonthView = currentYear === currentYearNow && selectedMonth === currentMonth;
    
    const weeks = [];
    let currentWeekStart = 1;
    const totalDays = mEnd.getDate();
    while (currentWeekStart <= totalDays) {
      const weekEnd = Math.min(currentWeekStart + 6, totalDays);
      weeks.push({ start: currentWeekStart, end: weekEnd });
      currentWeekStart = weekEnd + 1;
    }

    return (
      <div className="bg-gradient-to-br from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] rounded-2xl border border-[#1E1E26] overflow-hidden shadow-2xl">
        <div className={`px-5 py-4 border-b border-[#252530] flex items-center justify-between ${
          isCurrentMonthView ? 'bg-gradient-to-r from-[#E63946]/20 to-transparent' : 'bg-gradient-to-r from-[#1A1A22] to-[#15151A]'
        }`}>
          <span className={`text-xl font-black ${isCurrentMonthView ? 'text-[#E63946]' : 'text-white'}`}>
            {fullMonthNames[selectedMonth]} {currentYear}
            {isCurrentMonthView && <span className="ml-2 text-sm font-normal">● Current Month</span>}
          </span>
          <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30 rounded-full px-4">
            {monthCampaigns.length} Campaigns
          </Badge>
        </div>

        <div className="divide-y divide-[#1E1E26]">
          {weeks.map((week, wIndex) => {
            const weekStart = new Date(currentYear, selectedMonth, week.start);
            const weekEnd = new Date(currentYear, selectedMonth, week.end);
            const weekCampaigns = monthCampaigns.filter(c => {
              const cStart = new Date(c.startDate);
              const cEnd = new Date(c.endDate);
              return cStart <= weekEnd && cEnd >= weekStart;
            });
            const isTodayInWeek = isCurrentMonthView && currentDay >= week.start && currentDay <= week.end;

            return (
              <div 
                key={wIndex}
                className={`p-5 cursor-pointer transition-all ${
                  isTodayInWeek 
                    ? 'bg-gradient-to-r from-[#E63946]/10 to-transparent' 
                    : 'hover:bg-[#1A1A22]/30'
                }`}
                onClick={() => { setSelectedWeek(wIndex); setViewLevel('week'); }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-sm font-bold ${isTodayInWeek ? 'text-[#E63946]' : 'text-white'}`}>
                    Week {wIndex + 1}: {week.start} - {week.end} {monthNames[selectedMonth]}
                    {isTodayInWeek && <span className="ml-2 text-xs font-normal">● Today</span>}
                  </span>
                  <span className="text-xs text-[#6B7280]">{weekCampaigns.length} campaigns</span>
                </div>
                <div className="space-y-2">
                  {weekCampaigns.map(campaign => {
                    const typeConfig = campaignTypes[campaign.type];
                    const TypeIcon = typeConfig.icon;
                    const statusConf = statusConfig[campaign.status];
                    return (
                      <div 
                        key={campaign.id}
                        className="flex items-center gap-4 p-3 bg-gradient-to-r from-[#1A1A22] to-[#15151A] rounded-xl border border-[#252530] hover:border-[#3A3A45] transition-all"
                        onClick={(e) => { e.stopPropagation(); handleCampaignClick(campaign); }}
                      >
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${typeConfig.color}20` }}
                        >
                          <TypeIcon size={16} style={{ color: typeConfig.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-white truncate">{campaign.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#252530]">
                              <Building2 size={10} className="text-[#6B7280]" />
                              <span className="text-[10px] text-[#9CA3AF]">{campaign.ibName}</span>
                            </div>
                            <span className="text-[10px] text-[#6B7280]">{campaign.channel === 'vip' ? 'VIP' : 'Public'}</span>
                          </div>
                        </div>
                        <Badge 
                          className="text-[9px] flex-shrink-0 rounded-full"
                          style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}
                        >
                          {statusConf.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const totalDays = new Date(currentYear, selectedMonth + 1, 0).getDate();
    let weekStartDay = selectedWeek * 7 + 1;
    let weekEndDay = Math.min(weekStartDay + 6, totalDays);
    
    const weekStart = new Date(currentYear, selectedMonth, weekStartDay);
    const weekEnd = new Date(currentYear, selectedMonth, weekEndDay);
    
    const monthCampaigns = getCampaignsForPeriod(
      new Date(currentYear, selectedMonth, 1),
      new Date(currentYear, selectedMonth + 1, 0)
    );
    
    const weekCampaigns = monthCampaigns.filter(c => {
      const cStart = new Date(c.startDate);
      const cEnd = new Date(c.endDate);
      return cStart <= weekEnd && cEnd >= weekStart;
    });

    const days = [];
    for (let d = weekStartDay; d <= weekEndDay; d++) {
      const date = new Date(currentYear, selectedMonth, d);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const isToday = currentYear === currentYearNow && selectedMonth === currentMonth && d === currentDay;
      days.push({ day: d, dayName, date, isToday });
    }

    return (
      <div className="bg-gradient-to-br from-[#0F0F14] via-[#0A0A0E] to-[#0F0F14] rounded-2xl border border-[#1E1E26] overflow-hidden shadow-2xl">
        <div className="px-5 py-4 bg-gradient-to-r from-[#1A1A22] to-[#15151A] border-b border-[#252530] flex items-center justify-between">
          <span className="text-xl font-black text-white">
            Week {selectedWeek + 1}: {weekStartDay} - {weekEndDay} {fullMonthNames[selectedMonth]}
          </span>
          <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30 rounded-full px-4">
            {weekCampaigns.length} Campaigns
          </Badge>
        </div>

        <div className="grid border-b border-[#1E1E26]" style={{ gridTemplateColumns: `200px repeat(${days.length}, 1fr)` }}>
          <div className="p-4 border-r border-[#1E1E26] bg-[#0A0A0D]/50">
            <span className="text-[10px] font-bold text-[#6B7280] uppercase">Campaign</span>
          </div>
          {days.map((d, i) => (
            <div 
              key={i} 
              className={`p-3 text-center border-r border-[#1E1E26] last:border-r-0 ${
                d.isToday ? 'bg-[#E63946]/10' : 'bg-[#0A0A0D]/50'
              }`}
            >
              <p className={`text-[10px] ${d.isToday ? 'text-[#E63946]' : 'text-[#6B7280]'}`}>{d.dayName}</p>
              <p className={`text-lg font-black ${d.isToday ? 'text-[#E63946]' : 'text-white'}`}>{d.day}</p>
              {d.isToday && <div className="w-2 h-2 bg-[#E63946] rounded-full mx-auto mt-1" />}
            </div>
          ))}
        </div>

        <div className="divide-y divide-[#1E1E26]">
          {weekCampaigns.length === 0 ? (
            <div className="text-center py-16 text-[#6B7280]">No campaigns this week</div>
          ) : (
            weekCampaigns.map((campaign) => {
              const cStart = new Date(campaign.startDate);
              const cEnd = new Date(campaign.endDate);
              const typeConfig = campaignTypes[campaign.type];

              return (
                <div 
                  key={campaign.id}
                  className="grid hover:bg-[#1A1A22]/50 cursor-pointer"
                  style={{ gridTemplateColumns: `200px repeat(${days.length}, 1fr)` }}
                  onClick={() => handleCampaignClick(campaign)}
                >
                  <div className="p-4 border-r border-[#1E1E26]">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${typeConfig.color}30` }}
                      >
                        <typeConfig.icon size={16} style={{ color: typeConfig.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-white truncate">{campaign.name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Building2 size={10} className="text-[#6B7280]" />
                          <p className="text-[10px] text-[#6B7280] truncate">{campaign.ibName}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {days.map((d, i) => {
                    const cellDate = d.date;
                    const isActive = cStart <= cellDate && cEnd >= cellDate;

                    return (
                      <div 
                        key={i} 
                        className={`p-2 border-r border-[#1E1E26] last:border-r-0 ${
                          d.isToday ? 'bg-[#E63946]/5' : !isActive ? 'bg-[#0A0A0D]/30' : ''
                        }`}
                      >
                        {isActive && (
                          <div 
                            className="h-8 rounded-lg"
                            style={{ background: `linear-gradient(135deg, ${typeConfig.color}50 0%, ${typeConfig.color}30 100%)` }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const popupData = getStatsPopupData();

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))]">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[hsl(var(--surface))]/95 backdrop-blur-xl border-b border-[hsl(var(--border))]">
          <div className="px-4 lg:px-6 py-3 lg:py-4">
            {/* Title Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center shadow-lg">
                  <CalendarIcon size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-lg lg:text-xl font-bold tracking-tight text-[hsl(var(--text-primary))]" data-testid="page-title">
                    List of Campaign
                  </h1>
                  <p className="text-xs text-[hsl(var(--text-muted))]">All campaigns across all IBs</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => handleStatsClick('running')}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 text-xs font-semibold hover:bg-emerald-500/30 transition-all"
                  data-testid="stats-running"
                >
                  {runningCampaigns.length} Running
                </button>
                <button
                  onClick={() => handleStatsClick('upcoming')}
                  className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-500 border border-amber-500/30 text-xs font-semibold hover:bg-amber-500/30 transition-all"
                  data-testid="stats-upcoming"
                >
                  {upcomingCampaigns.length} Upcoming
                </button>
                <button
                  onClick={() => handleStatsClick('total')}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#3B82F6]/20 to-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30 text-sm font-bold hover:from-[#3B82F6]/30 transition-all"
                  data-testid="stats-total"
                >
                  {filteredCampaigns.length} Total
                </button>
              </div>
            </div>

            {/* Quick Filters & Search */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-1 bg-gradient-to-r from-[#151519] to-[#0F0F12] rounded-xl border border-[#252530] p-1">
                {quickFilters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setQuickFilter(filter.id)}
                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                      quickFilter === filter.id
                        ? 'bg-gradient-to-r from-[#E63946] to-[#D62839] text-white'
                        : 'text-[#6B7280] hover:text-white hover:bg-[#252530]/50'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search campaigns..."
                  className="pl-9 h-9 bg-gradient-to-r from-[#151519] to-[#0F0F12] border-[#252530] text-white text-xs rounded-xl"
                />
              </div>

              <Button variant="outline" size="sm" onClick={handleExport} className="border-[#252530] text-[#9CA3AF] hover:text-white rounded-xl" data-testid="export-btn">
                <Download size={14} className="mr-1.5" />
                Export
              </Button>
            </div>

            {/* Controls Row - REMOVED LABELS */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Back button */}
                {viewLevel !== 'timeline' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goBack}
                    className="border-[#252530] text-[#9CA3AF] hover:text-white rounded-xl"
                    data-testid="back-btn"
                  >
                    <ChevronLeft size={14} className="mr-1" />
                    Back
                  </Button>
                )}

                {/* Year navigation */}
                <div className="flex items-center gap-1 bg-gradient-to-r from-[#151519] to-[#0F0F12] rounded-xl border border-[#252530] p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentYear(y => y - 1)}
                    className="w-8 h-8 p-0 text-[#6B7280] hover:text-white rounded-lg"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="px-4 text-sm font-black text-white">{currentYear}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentYear(y => y + 1)}
                    className="w-8 h-8 p-0 text-[#6B7280] hover:text-white rounded-lg"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>

                {/* Quarter navigation */}
                {viewLevel === 'quarter' && (
                  <div className="flex items-center gap-1 bg-gradient-to-r from-[#151519] to-[#0F0F12] rounded-xl border border-[#252530] p-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedQuarter(q => Math.max(0, q - 1))}
                      disabled={selectedQuarter === 0}
                      className="w-8 h-8 p-0 text-[#6B7280] hover:text-white rounded-lg disabled:opacity-30"
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="px-3 text-sm font-bold text-[#E63946]">Q{selectedQuarter + 1}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedQuarter(q => Math.min(3, q + 1))}
                      disabled={selectedQuarter === 3}
                      className="w-8 h-8 p-0 text-[#6B7280] hover:text-white rounded-lg disabled:opacity-30"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                )}

                {/* Month navigation */}
                {viewLevel === 'month' && (
                  <div className="flex items-center gap-1 bg-gradient-to-r from-[#151519] to-[#0F0F12] rounded-xl border border-[#252530] p-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedMonth(m => Math.max(0, m - 1))}
                      disabled={selectedMonth === 0}
                      className="w-8 h-8 p-0 text-[#6B7280] hover:text-white rounded-lg disabled:opacity-30"
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="px-3 text-sm font-bold text-[#8B5CF6]">{monthNames[selectedMonth]}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedMonth(m => Math.min(11, m + 1))}
                      disabled={selectedMonth === 11}
                      className="w-8 h-8 p-0 text-[#6B7280] hover:text-white rounded-lg disabled:opacity-30"
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                {viewLevel === 'timeline' && (
                  <div className="flex items-center gap-1 bg-gradient-to-r from-[#151519] to-[#0F0F12] rounded-xl border border-[#252530] p-1">
                    <button
                      onClick={() => setViewMode('timeline')}
                      className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-all ${
                        viewMode === 'timeline' ? 'bg-[#E63946] text-white' : 'text-[#6B7280] hover:text-white'
                      }`}
                    >
                      <LayoutGrid size={14} />
                      Timeline
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      data-testid="view-mode-list-btn"
                      className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-all ${
                        viewMode === 'list' ? 'bg-[#E63946] text-white' : 'text-[#6B7280] hover:text-white'
                      }`}
                    >
                      <List size={14} />
                      List
                    </button>
                  </div>
                )}

                {/* Filters */}
                <Select value={filterIB} onValueChange={setFilterIB}>
                  <SelectTrigger className="w-40 bg-gradient-to-r from-[#151519] to-[#0F0F12] border-[#252530] text-white h-9 rounded-xl text-xs">
                    <Building2 size={12} className="mr-1.5 text-[#6B7280]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151519] border-[#252530]">
                    {ibList.map(ib => (
                      <SelectItem key={ib.id} value={ib.id} className="text-white text-xs">{ib.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36 bg-gradient-to-r from-[#151519] to-[#0F0F12] border-[#252530] text-white h-9 rounded-xl text-xs">
                    <Filter size={12} className="mr-1.5 text-[#6B7280]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151519] border-[#252530]">
                    <SelectItem value="all" className="text-white text-xs">All Status</SelectItem>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-white text-xs">{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {viewLevel === 'timeline' && viewMode === 'timeline' && renderTimelineView()}
          {viewLevel === 'timeline' && viewMode === 'list' && renderListView()}
          {viewLevel === 'quarter' && renderQuarterView()}
          {viewLevel === 'month' && renderMonthView()}
          {viewLevel === 'week' && renderWeekView()}
        </div>

        {/* Modals */}
        <CampaignDetailModal
          campaign={selectedCampaign}
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          onNavigate={handleNavigateToIB}
        />

        <CampaignListPopup
          title={popupData.title}
          campaigns={popupData.campaigns}
          open={statsPopup.open}
          onClose={() => setStatsPopup({ open: false, type: null })}
          onCampaignClick={handleCampaignClick}
          color={popupData.color}
        />
      </div>
    </ResponsiveLayout>
  );
}
